import React, { useState } from 'react';
import { 
  Upload, Download, FileText, AlertCircle, CheckCircle, 
  X, Eye, RefreshCw, FileSpreadsheet, Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const EmployeeImportExport = ({ onClose, onImportComplete }) => {
  const [activeTab, setActiveTab] = useState('import');
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileSpreadsheet className="w-5 h-5 mr-2 text-yellow-600" />
            Employee Import/Export
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="import">Import Employees</TabsTrigger>
            <TabsTrigger value="export">Export Employees</TabsTrigger>
          </TabsList>
          
          <TabsContent value="import">
            <ImportTab onImportComplete={onImportComplete} />
          </TabsContent>
          
          <TabsContent value="export">
            <ExportTab />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Import Tab Component
const ImportTab = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [step, setStep] = useState('upload'); // upload, preview, import, result

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    
    // Parse and preview file
    try {
      const preview = await parseCSVFile(selectedFile);
      setPreviewData(preview);
      setStep('preview');
    } catch (error) {
      console.error('Failed to parse file:', error);
      toast({
        title: 'Error',
        description: 'Failed to parse the selected file. Please check the format.',
        variant: 'destructive'
      });
    }
  };

  const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error('File must contain at least a header and one data row'));
            return;
          }
          
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const rows = lines.slice(1, 11).map(line => { // Preview first 10 rows
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
          
          resolve({ headers, rows, totalRows: lines.length - 1 });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setImporting(true);
      setStep('import');
      
      // Parse full file for import
      const fullData = await parseFullCSVFile(file);
      const importRecords = fullData.map(row => ({
        first_name: row['First Name'] || row['first_name'] || '',
        last_name: row['Last Name'] || row['last_name'] || '',
        email: row['Email'] || row['email'] || '',
        position_title: row['Position'] || row['position_title'] || '',
        department: row['Department'] || row['department'] || '',
        school_name: row['School'] || row['school_name'] || '',
        hire_date: row['Hire Date'] || row['hire_date'] || '',
        fte: parseFloat(row['FTE'] || row['fte'] || '1.0'),
        phone: row['Phone'] || row['phone'] || ''
      }));

      const result = await apiService.makeRequest('/api/employees/import', {
        method: 'POST',
        body: JSON.stringify(importRecords)
      });

      setImportResult(result);
      setStep('result');
      
      if (result.imported_count > 0) {
        toast({
          title: 'Import Completed',
          description: `Successfully imported ${result.imported_count} employees.`
        });
        onImportComplete?.();
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: 'Import Failed',
        description: 'Failed to import employees. Please check the file format.',
        variant: 'destructive'
      });
      // Reset to preview step to allow retry while keeping parsed data
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const parseFullCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const rows = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  };

  const downloadTemplate = () => {
    const template = `First Name,Last Name,Email,Position,Department,School,Hire Date,FTE,Phone
John,Smith,john.smith@mba.edu,Mathematics Teacher,Teaching Staff,Lincoln Elementary,2023-08-15,1.0,555-0123
Jane,Doe,jane.doe@mba.edu,Science Teacher,Teaching Staff,Washington Middle,2023-08-20,0.8,555-0456`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (step === 'upload') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Employee Data</h3>
          <p className="text-gray-600 mb-4">
            Upload a CSV file with employee information to bulk import employees.
          </p>
        </div>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Required Columns</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Your CSV file should include: First Name, Last Name, Email (required), 
                  Position, Department, School, Hire Date, FTE, Phone
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <div>
            <Label>Select CSV File</Label>
            <Input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="mt-1"
            />
          </div>

          <div className="flex justify-center">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'preview') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Preview Import Data</h3>
            <p className="text-gray-600">
              Review the first 10 rows. Total rows to import: {previewData.totalRows}
            </p>
          </div>
          <Button variant="outline" onClick={() => setStep('upload')}>
            <X className="w-4 h-4 mr-2" />
            Change File
          </Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {previewData.headers.map((header, index) => (
                    <th key={index} className="px-3 py-2 text-left font-medium text-gray-900">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {previewData.rows.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {previewData.headers.map((header, colIndex) => (
                      <td key={colIndex} className="px-3 py-2 text-gray-900">
                        {row[header] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => setStep('upload')}>
            Back
          </Button>
          <Button 
            onClick={handleImport}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import {previewData.totalRows} Employees
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'import') {
    return (
      <div className="space-y-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Importing Employees</h3>
          <p className="text-gray-600">Please wait while we process your employee data...</p>
        </div>
      </div>
    );
  }

  if (step === 'result' && importResult) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Complete</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{importResult.total_records}</div>
              <div className="text-sm text-gray-600">Total Records</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{importResult.imported_count}</div>
              <div className="text-sm text-gray-600">Imported</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{importResult.skipped_count}</div>
              <div className="text-sm text-gray-600">Skipped</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{importResult.error_count}</div>
              <div className="text-sm text-gray-600">Errors</div>
            </CardContent>
          </Card>
        </div>

        {importResult.validation_errors && importResult.validation_errors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Validation Errors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {importResult.validation_errors.slice(0, 10).map((error, index) => (
                  <div key={index} className="text-sm">
                    <span className="font-medium">Row {index + 2}:</span> {error.errors.join(', ')}
                  </div>
                ))}
                {importResult.validation_errors.length > 10 && (
                  <p className="text-sm text-gray-500">
                    ... and {importResult.validation_errors.length - 10} more errors
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button 
            onClick={() => {
              setStep('upload');
              setFile(null);
              setPreviewData(null);
              setImportResult(null);
            }}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            Import More Employees
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

// Export Tab Component
const ExportTab = () => {
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportFilters, setExportFilters] = useState({
    include_inactive: false,
    include_terminated: false,
    school_id: '',
    department: ''
  });

  const handleExport = async () => {
    try {
      setExporting(true);
      
      // This would typically call an API endpoint that generates and returns the file
      const response = await apiService.makeRequest('/api/employees/export', {
        method: 'POST',
        body: JSON.stringify({
          format: exportFormat,
          filters: exportFilters
        })
      });

      // Create and download file
      const blob = new Blob([response.data], { 
        type: exportFormat === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: 'Employee data has been exported successfully.'
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export employee data.',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Download className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Export Employee Data</h3>
        <p className="text-gray-600">
          Download employee information in CSV or Excel format.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Export Format</Label>
          <div className="flex space-x-4 mt-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={(e) => setExportFormat(e.target.value)}
                className="mr-2"
              />
              CSV File
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="xlsx"
                checked={exportFormat === 'xlsx'}
                onChange={(e) => setExportFormat(e.target.value)}
                className="mr-2"
              />
              Excel File
            </label>
          </div>
        </div>

        <div>
          <Label>Include Options</Label>
          <div className="space-y-2 mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportFilters.include_inactive}
                onChange={(e) => setExportFilters(prev => ({ 
                  ...prev, 
                  include_inactive: e.target.checked 
                }))}
                className="mr-2"
              />
              Include inactive employees
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportFilters.include_terminated}
                onChange={(e) => setExportFilters(prev => ({ 
                  ...prev, 
                  include_terminated: e.target.checked 
                }))}
                className="mr-2"
              />
              Include terminated employees
            </label>
          </div>
        </div>

        <div className="flex justify-center">
          <Button 
            onClick={handleExport}
            disabled={exporting}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export Employee Data
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeImportExport;